import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface PublicSalesImage {
  url: string;
  thumbnail_url: string | null;
  display_order: number;
}
export interface PublicImageGroup {
  purpose: string;
  language: string;
  category: string | null;
  display_order: number;
  images: PublicSalesImage[];
}
export interface PublicUnitFloorPlans {
  unit_id: string;
  image_groups: PublicImageGroup[];
}
export interface PublicText {
  purpose: string;
  language: 'ko' | 'en' | 'und';
  value: string | null;
  order: number | null;
}
export interface PublicTypePresentation {
  texts: PublicText[] | null;
  furniture: string[] | null;
  facilities: string[] | null;
}
export interface PublicPropertyPresentation {
  texts: PublicText[] | null;
  components:
    | {
        kind: string;
        parent_index?: number | null;
        line_code?: string | null;
        order: number | null;
        texts: PublicText[];
        images: PublicImageGroup[];
      }[]
    | null;
  faqs: {
    id: string;
    question: { ko: string | null; en: string | null };
    answer: { ko: string | null; en: string | null };
    order: number | null;
  }[];
}
export interface PublicSummary {
  property_id: string;
  property_name: string;
  sold_out: boolean;
  available_room_count: number;
  minimum_daily_price_minor?: number | null;
  display_price_basis?: string;
  card_images?: PublicSalesImage[];
  hero_images?: PublicSalesImage[];
  presentation?: PublicPropertyPresentation | null;
  room_types?: {
    room_type_id: string;
    room_type_name: string;
    room_type_images: PublicImageGroup[];
    presentation?: PublicTypePresentation | null;
  }[];
}
interface PublicOffer {
  property_id: string;
  room_id: string;
  room_type_id: string;
  first_date: string;
  last_date: string;
  daily_price_minor: number;
  total_amount_minor: number;
  currency: string;
}
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '공개 판매 결과를 확인하지 못했습니다.',
  });
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const images = (v: unknown): v is PublicSalesImage[] =>
  Array.isArray(v) &&
  v.length <= 4096 &&
  v.every(
    (x) =>
      record(x) &&
      typeof x.url === 'string' &&
      (x.thumbnail_url === null || typeof x.thumbnail_url === 'string') &&
      Number.isSafeInteger(x.display_order),
  );
const imageGroups = (v: unknown) =>
  Array.isArray(v) &&
  v.length <= 4096 &&
  v.every(
    (g) =>
      record(g) &&
      typeof g.purpose === 'string' &&
      typeof g.language === 'string' &&
      (g.category === null || typeof g.category === 'string') &&
      Number.isSafeInteger(g.display_order) &&
      images(g.images),
  );
const unitFloorPlans = (v: unknown): v is PublicUnitFloorPlans[] =>
  Array.isArray(v) &&
  v.length <= 4096 &&
  v.every((unit) => record(unit) && isUuid(unit.unit_id) && imageGroups(unit.image_groups)) &&
  new Set(v.map((unit) => (unit as PublicUnitFloorPlans).unit_id.toLowerCase())).size === v.length;
const nullableText = (v: unknown) => v === null || typeof v === 'string';
const nullableOrder = (v: unknown) => v === null || Number.isSafeInteger(v);
const texts = (v: unknown): v is PublicText[] | null =>
  v === null ||
  (Array.isArray(v) &&
    v.every(
      (t) =>
        record(t) &&
        typeof t.purpose === 'string' &&
        typeof t.language === 'string' &&
        ['ko', 'en', 'und'].includes(t.language) &&
        nullableText(t.value) &&
        nullableOrder(t.order),
    ));
const localized = (v: unknown) => record(v) && nullableText(v.ko) && nullableText(v.en);
const propertyPresentation = (v: unknown) =>
  v === undefined ||
  v === null ||
  (record(v) &&
    texts(v.texts) &&
    (v.components === null ||
      (Array.isArray(v.components) &&
        v.components.every(
          (c, _index, components) =>
            record(c) &&
            typeof c.kind === 'string' &&
            (c.line_code === undefined || nullableText(c.line_code)) &&
            (c.parent_index === undefined ||
              c.parent_index === null ||
              (typeof c.parent_index === 'number' &&
                Number.isSafeInteger(c.parent_index) &&
                c.parent_index >= 0 &&
                c.parent_index < components.length &&
                c.kind === 'LOCATION_LINE' &&
                record(components[c.parent_index]) &&
                (components[c.parent_index] as Record<string, unknown>).kind ===
                  'LOCATION_BULLET')) &&
            nullableOrder(c.order) &&
            Array.isArray(c.texts) &&
            texts(c.texts) &&
            imageGroups(c.images),
        ))) &&
    Array.isArray(v.faqs) &&
    v.faqs.every(
      (f) =>
        record(f) &&
        isUuid(f.id) &&
        localized(f.question) &&
        localized(f.answer) &&
        nullableOrder(f.order),
    ));
const stringArray = (v: unknown) =>
  v === null || (Array.isArray(v) && v.every((x) => typeof x === 'string'));
const typePresentation = (v: unknown) =>
  v === undefined ||
  v === null ||
  (record(v) && texts(v.texts) && stringArray(v.furniture) && stringArray(v.facilities));
const summary = (v: unknown): v is PublicSummary =>
  record(v) &&
  isUuid(String(v.property_id)) &&
  typeof v.property_name === 'string' &&
  typeof v.sold_out === 'boolean' &&
  Number.isSafeInteger(v.available_room_count) &&
  (v.minimum_daily_price_minor === undefined ||
    v.minimum_daily_price_minor === null ||
    (Number.isSafeInteger(v.minimum_daily_price_minor) &&
      Number(v.minimum_daily_price_minor) >= 0)) &&
  (v.display_price_basis === undefined || typeof v.display_price_basis === 'string') &&
  (v.card_images === undefined || images(v.card_images)) &&
  (v.hero_images === undefined || images(v.hero_images)) &&
  propertyPresentation(v.presentation) &&
  (v.room_types === undefined ||
    (Array.isArray(v.room_types) &&
      v.room_types.every(
        (t) =>
          record(t) &&
          isUuid(t.room_type_id) &&
          typeof t.room_type_name === 'string' &&
          imageGroups(t.room_type_images) &&
          typePresentation(t.presentation),
      )));
const offer = (v: unknown): v is PublicOffer =>
  record(v) &&
  isUuid(String(v.room_id)) &&
  isUuid(String(v.room_type_id)) &&
  isUuid(String(v.property_id)) &&
  Number.isSafeInteger(v.daily_price_minor) &&
  Number.isSafeInteger(v.total_amount_minor) &&
  v.currency === 'KRW' &&
  typeof v.first_date === 'string' &&
  typeof v.last_date === 'string';
const periodParams = (first: string, last: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(first) || !/^\d{4}-\d{2}-\d{2}$/.test(last) || first > last)
    throw invalid();
  return { first_date: first, last_date: last };
};
export async function readPublicMidtermSales(
  client: ServiceClient,
  first: string,
  last: string,
  signal?: AbortSignal,
) {
  const items: PublicSummary[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  do {
    const value = await client.get<unknown>('sales/midterm/properties', {
      params: { ...periodParams(first, last), ...(cursor ? { cursor } : {}) },
      signal,
      preserveEnvelope: true,
    });
    if (
      !record(value) ||
      !Array.isArray(value.data) ||
      !value.data.every(summary) ||
      (value.next_cursor !== null && typeof value.next_cursor !== 'string')
    )
      throw invalid();
    items.push(...value.data);
    cursor = typeof value.next_cursor === 'string' ? value.next_cursor : undefined;
    if (cursor && seen.has(cursor)) throw invalid();
    if (cursor) seen.add(cursor);
  } while (cursor);
  return items;
}
export async function readPublicMidtermProperty(
  client: ServiceClient,
  propertyId: string,
  first: string,
  last: string,
  signal?: AbortSignal,
) {
  if (!isUuid(propertyId)) throw invalid();
  const value = await client.get<unknown>(`sales/midterm/properties/${propertyId}`, {
    params: periodParams(first, last),
    signal,
    preserveEnvelope: true,
  });
  if (
    !record(value) ||
    !summary(value.data) ||
    value.data.property_id !== propertyId ||
    !Array.isArray(value.rooms) ||
    !value.rooms.every(offer)
  )
    throw invalid();
  return { property: value.data, rooms: value.rooms };
}

export async function readRoomMidtermQuote(
  client: ServiceClient,
  roomId: string,
  first: string,
  last: string,
) {
  if (!isUuid(roomId)) throw invalid();
  const response = await client.get<unknown>(`sales/rooms/${roomId}/quote`, {
    params: periodParams(first, last),
    preserveEnvelope: true,
  });
  const value = record(response) ? response.data : null;
  if (
    !record(value) ||
    value.room_id !== roomId ||
    (value.floor_plans !== undefined && !imageGroups(value.floor_plans)) ||
    (value.unit_floor_plans !== undefined && !unitFloorPlans(value.unit_floor_plans)) ||
    value.currency !== 'KRW' ||
    value.first_date !== first ||
    value.last_date !== last ||
    !Number.isSafeInteger(value.daily_price_minor) ||
    Number(value.daily_price_minor) <= 0 ||
    !Number.isSafeInteger(value.accommodation_amount_minor) ||
    !Number.isSafeInteger(value.deposit_amount_minor) ||
    !Number.isSafeInteger(value.cleaning_amount_minor) ||
    !Number.isSafeInteger(value.payable_total_minor) ||
    Number(value.accommodation_amount_minor) <= 0 ||
    Number(value.deposit_amount_minor) < 0 ||
    Number(value.cleaning_amount_minor) < 0 ||
    Number(value.payable_total_minor) !==
      Number(value.accommodation_amount_minor) +
        Number(value.deposit_amount_minor) +
        Number(value.cleaning_amount_minor)
  )
    throw invalid();
  return {
    floorPlans: value.floor_plans as PublicImageGroup[] | undefined,
    unitFloorPlans: value.unit_floor_plans,
    daily: Number(value.daily_price_minor),
    accommodation: Number(value.accommodation_amount_minor),
    deposit: Number(value.deposit_amount_minor),
    cleaning: Number(value.cleaning_amount_minor),
    payable: Number(value.payable_total_minor),
  };
}
